import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { getMembers, Member, Pagination } from '../services/api';

const MembersPage: React.FC = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState<Member[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadMembers();
  }, [page, search, tierFilter]);

  const loadMembers = async () => {
    setIsLoading(true);
    try {
      const result = await getMembers({
        page,
        limit: 20,
        search: search || undefined,
        tier: tierFilter || undefined,
      });
      setMembers(result.members);
      setPagination(result.pagination);
    } catch (err) {
      console.error('Failed to load members:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const getTierBadge = (tier: string) => {
    const styles = {
      Gold: 'bg-yellow-100 text-yellow-800',
      Silver: 'bg-gray-100 text-gray-800',
      Bronze: 'bg-orange-100 text-orange-800',
    };
    return styles[tier as keyof typeof styles] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatPhone = (phone: string) => {
    // Format: +1XXXXXXXXXX -> (XXX) XXX-XXXX
    const digits = phone.replace(/\D/g, '').slice(-10);
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-buggy-dark">Members</h1>
        {pagination && (
          <span className="text-buggy-gray">{pagination.total} total members</span>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name, phone, or email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-buggy-blue focus:border-transparent outline-none"
            />
          </div>
          <select
            value={tierFilter}
            onChange={(e) => { setTierFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-buggy-blue focus:border-transparent outline-none"
          >
            <option value="">All Tiers</option>
            <option value="Bronze">Bronze</option>
            <option value="Silver">Silver</option>
            <option value="Gold">Gold</option>
          </select>
          <button
            type="submit"
            className="bg-buggy-blue text-white px-4 py-2 rounded-lg hover:bg-buggy-blue-light transition"
          >
            Search
          </button>
        </form>
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-buggy-gray">Loading...</div>
        ) : members.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-buggy-gray">No members found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-buggy-gray">Member</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-buggy-gray">Phone</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-buggy-gray">Tier</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-buggy-gray">Points</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-buggy-gray">Enrolled</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {members.map((member) => (
                    <tr
                      key={member.memberId}
                      onClick={() => navigate(`/members/${member.memberId}`)}
                      className="hover:bg-gray-50 cursor-pointer transition"
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-buggy-dark">
                            {member.firstName} {member.lastName}
                          </p>
                          {member.email && (
                            <p className="text-sm text-buggy-gray">{member.email}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-buggy-dark">
                        {formatPhone(member.phoneNumber)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getTierBadge(member.currentTier)}`}>
                          {member.currentTier}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-medium text-buggy-blue">{member.currentPoints}</span>
                        <span className="text-buggy-gray text-sm"> / {member.lifetimePoints}</span>
                      </td>
                      <td className="px-4 py-3 text-buggy-gray text-sm">
                        {formatDate(member.enrolledDate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-buggy-gray">
                  Showing {(page - 1) * pagination.limit + 1} to{' '}
                  {Math.min(page * pagination.limit, pagination.total)} of {pagination.total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="px-3 py-2 text-sm">
                    Page {page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                    disabled={page === pagination.totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MembersPage;
